import React, { useState, useEffect, useCallback } from 'react';
import { usuariosApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, ShieldCheck, ToggleLeft, ToggleRight } from 'lucide-react';

const ROLES = [
  { value: 'admin', label: 'Admin', descricao: 'Acesso total' },
  { value: 'financeiro', label: 'Financeiro', descricao: 'Dashboard, Financeiro, Orçamentos, PIX' },
  { value: 'atendente', label: 'Atendente', descricao: 'Clientes, Orçamentos, Agendamento, Serviços' }
];

const roleColor = (r) => ({
  admin: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  financeiro: 'bg-blue-100 text-blue-800 border-blue-300',
  atendente: 'bg-amber-100 text-amber-800 border-amber-300'
}[r] || 'bg-gray-100 text-gray-800');

const emptyUser = {
  nome: '',
  email: '',
  telefone: '',
  password: '',
  role: 'atendente',
  ativo: true
};

const Usuarios = () => {
  const { user: currentUser } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState({ ...emptyUser });
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await usuariosApi.list();
      setUsuarios(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleOpenForm = (u = null) => {
    if (u) {
      setSelected(u);
      setFormData({
        nome: u.nome,
        email: u.email,
        telefone: u.telefone || '',
        password: '',
        role: u.role,
        ativo: u.ativo
      });
    } else {
      setSelected(null);
      setFormData({ ...emptyUser });
    }
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim() || !formData.email.trim()) {
      toast.error('Nome e e-mail são obrigatórios');
      return;
    }
    if (!selected && !formData.password) {
      toast.error('Informe uma senha inicial');
      return;
    }
    if (formData.password && formData.password.length < 6) {
      toast.error('Senha deve ter no mínimo 6 caracteres');
      return;
    }
    setSaving(true);
    try {
      if (selected) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await usuariosApi.update(selected.id, payload);
        toast.success('Usuário atualizado!');
      } else {
        await usuariosApi.create(formData);
        toast.success('Usuário criado!');
      }
      setShowForm(false);
      setSelected(null);
      setFormData({ ...emptyUser });
      loadData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Erro ao salvar usuário');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAtivo = async (u) => {
    if (u.id === currentUser?.id) {
      toast.error('Você não pode desativar seu próprio usuário');
      return;
    }
    try {
      await usuariosApi.update(u.id, { ativo: !u.ativo });
      toast.success(u.ativo ? 'Usuário desativado' : 'Usuário ativado');
      loadData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await usuariosApi.delete(toDelete.id);
      toast.success('Usuário excluído');
      setToDelete(null);
      loadData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  return (
    <div className="space-y-6" data-testid="usuarios-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans' }}>
            Usuários
          </h1>
          <p className="text-gray-500 mt-1">
            Gerencie funcionários internos e defina permissões do sistema.
          </p>
        </div>
        <Button onClick={() => handleOpenForm()} data-testid="add-usuario-btn">
          <Plus size={18} className="mr-2" />
          Novo usuário
        </Button>
      </div>

      {/* Roles info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {ROLES.map((r) => (
          <div key={r.value} className={`p-3 rounded-lg border ${roleColor(r.value)}`}>
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} />
              <span className="font-semibold">{r.label}</span>
            </div>
            <p className="text-xs mt-1 opacity-90">{r.descricao}</p>
          </div>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Work Sans' }}>Lista de Usuários</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
          ) : usuarios.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Nenhum usuário cadastrado</div>
          ) : (
            <div className="table-fit">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>E-mail</th>
                    <th>Telefone</th>
                    <th>Papel</th>
                    <th>Status</th>
                    <th>Criado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map((u) => {
                    const isSelf = u.id === currentUser?.id;
                    return (
                      <tr key={u.id} data-testid={`user-row-${u.id}`}>
                        <td className="font-medium">
                          {u.nome}
                          {isSelf && <Badge variant="outline" className="ml-2 text-xs">Você</Badge>}
                        </td>
                        <td>{u.email}</td>
                        <td>{u.telefone || '—'}</td>
                        <td>
                          <Badge className={`${roleColor(u.role)} border`} variant="outline">
                            {ROLES.find((r) => r.value === u.role)?.label || u.role}
                          </Badge>
                        </td>
                        <td>
                          {u.ativo ? (
                            <span className="text-emerald-600 text-sm font-medium">Ativo</span>
                          ) : (
                            <span className="text-gray-400 text-sm">Inativo</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap text-sm text-gray-500">
                          {u.created_at ? formatDate(u.created_at.split('T')[0]) : '—'}
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleAtivo(u)}
                              disabled={isSelf}
                              title={u.ativo ? 'Desativar' : 'Ativar'}
                              data-testid={`toggle-ativo-${u.id}`}
                            >
                              {u.ativo
                                ? <ToggleRight size={18} className="text-emerald-600" />
                                : <ToggleLeft size={18} className="text-gray-400" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenForm(u)}
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setToDelete(u)}
                              disabled={isSelf}
                              className="text-red-600 hover:text-red-700 disabled:text-gray-300"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }}>
              {selected ? 'Editar usuário' : 'Novo usuário'}
            </DialogTitle>
            <DialogDescription>
              {selected
                ? 'Deixe a senha em branco para mantê-la inalterada.'
                : 'Preencha os dados do funcionário e defina seu papel no sistema.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="user-nome">Nome completo *</Label>
              <Input
                id="user-nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
                data-testid="user-nome-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="user-email">E-mail *</Label>
                <Input
                  id="user-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="user-telefone">Telefone</Label>
                <Input
                  id="user-telefone"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder="(31) 99999-9999"
                  data-testid="user-telefone-input"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="user-password">
                Senha {selected ? '(deixe em branco para não alterar)' : '*'}
              </Label>
              <Input
                id="user-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={selected ? '••••••' : 'Mínimo 6 caracteres'}
                data-testid="user-password-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Papel *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(v) => setFormData({ ...formData, role: v })}
                  disabled={selected && selected.id === currentUser?.id}
                >
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selected && selected.id === currentUser?.id && (
                  <p className="text-xs text-amber-600">Você não pode alterar seu próprio papel</p>
                )}
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <label className="flex items-center gap-2 h-10 px-3 border rounded-md cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.ativo}
                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                    className="w-4 h-4"
                    disabled={selected && selected.id === currentUser?.id}
                    data-testid="user-ativo-checkbox"
                  />
                  <span className="text-sm">{formData.ativo ? 'Ativo' : 'Inativo'}</span>
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#2B7A3D] hover:bg-[#1f5a2c] text-white" data-testid="save-user-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando...</> : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir {toDelete?.nome}? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-user-btn">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Usuarios;
